import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Check, Clock, Info, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import axios from "axios";
import { useAuth, API } from "../App";

const FocusSelection = () => {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [loadingGoal, setLoadingGoal] = useState(null);
  const [userData, setUserData] = useState(null);
  const [goalProfiles, setGoalProfiles] = useState({});
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [showIntensityModal, setShowIntensityModal] = useState(false);

  const goals = [
    { 
      id: "perder", 
      label: "Perder Peso", 
      desc: "Défice calórico para queima de gordura", 
      emoji: "🔥"
    },
    { 
      id: "manter", 
      label: "Manter Saúde", 
      desc: "Equilíbrio e longevidade", 
      emoji: "⚖️"
    },
    { 
      id: "ganhar", 
      label: "Ganhar Peso", 
      desc: "Superávit para massa magra", 
      emoji: "💪"
    }
  ];

  const intensities = [
    { id: "saudavel", label: "Saudável", desc: "Mudanças graduais" },
    { id: "moderado", label: "Moderado", desc: "Equilíbrio" },
    { id: "agressivo", label: "Agressivo", desc: "Resultados rápidos" }
  ];

  // Load user profile and goal profiles on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        const [profileRes, goalsRes] = await Promise.all([
          axios.get(`${API}/users/profile`),
          axios.get(`${API}/users/goal-profiles`)
        ]);
        setUserData(profileRes.data);
        setGoalProfiles(goalsRes.data.profiles || {});
      } catch (error) {
        console.error('Error loading data:', error);
      }
    };
    loadData();
  }, []);

  const handleGoalSelect = (goalId) => {
    // Para "Manter Peso", vai direto sem pedir intensidade
    if (goalId === 'manter') {
      handleGoalWithIntensity(goalId, 'moderado');
      return;
    }
    
    const profiles = goalProfiles[goalId] || {};
    const configuredIntensities = Object.keys(profiles);
    
    // If only one intensity configured, go directly
    if (configuredIntensities.length === 1) {
      handleGoalWithIntensity(goalId, configuredIntensities[0]);
    } else if (configuredIntensities.length > 1 || goalId === userData?.goal) {
      // Multiple intensities or current goal - show modal
      setSelectedGoal(goalId);
      setShowIntensityModal(true);
    } else {
      // No profiles for this goal - go to onboarding silently
      navigate('/onboarding', { state: { goal: goalId } });
    }
  };

  const handleGoalWithIntensity = async (goalId, intensityId) => {
    setLoadingGoal(goalId);
    setLoading(true);
    setShowIntensityModal(false);
    
    try {
      const response = await axios.post(`${API}/users/switch-goal`, { 
        goal: goalId,
        intensity: intensityId
      });
      
      if (response.data.needs_onboarding) {
        navigate('/onboarding', { state: { goal: goalId, intensity: intensityId } });
      } else {
        updateUser({ 
          goal: goalId,
          daily_calories: response.data.daily_calories
        });
        
        const goalLabel = goals.find(g => g.id === goalId)?.label;
        const intensityLabel = intensities.find(i => i.id === intensityId)?.label;
        let successMsg = `${goalLabel} (${intensityLabel})!`;
        
        if (response.data.weeks_to_goal > 0) {
          successMsg += ` Meta em ${response.data.weeks_to_goal} semanas.`;
        }
        
        toast.success(successMsg);
        navigate('/');
      }
    } catch (error) {
      console.error('Goal selection error:', error);
      navigate('/onboarding', { state: { goal: goalId, intensity: intensityId } });
    } finally {
      setLoading(false);
      setLoadingGoal(null);
    }
  };

  const handleNewIntensity = () => {
    setShowIntensityModal(false);
    navigate('/onboarding', { state: { goal: selectedGoal } });
  };

  const currentGoal = userData?.goal || user?.goal;
  const currentIntensity = userData?.intensity || user?.intensity;
  
  // Check if a goal has any profile
  const hasAnyProfile = (goalId) => {
    return goalProfiles[goalId] && Object.keys(goalProfiles[goalId]).length > 0;
  };

  // Get configured intensities for a goal
  const getConfiguredIntensities = (goalId) => {
    return Object.keys(goalProfiles[goalId] || {});
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="bg-primary text-white p-6 text-center">
        <h1 className="text-2xl font-bold">Nzadi Fit</h1>
        <p className="text-white/80 text-sm mt-1">Constrói o corpo dos teus sonhos</p>
      </header>

      {/* Content */}
      <main className="flex-1 flex flex-col items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          {/* Welcome */}
          <div className="text-center mb-8">
            <div className="text-5xl mb-4">🎯</div>
            <h2 className="text-2xl font-bold text-foreground">
              {userData?.has_completed_onboarding 
                ? `Olá, ${user?.name?.split(' ')[0] || 'Campeão(a)'}!`
                : `Bem-vindo(a), ${user?.name?.split(' ')[0] || 'Amigo(a)'}!`
              }
            </h2>
            <p className="text-muted-foreground mt-2">
              {userData?.has_completed_onboarding 
                ? 'Qual é o teu foco hoje?'
                : 'Vamos configurar o teu perfil'
              }
            </p>
          </div>

          {/* Goal Options */}
          <div className="space-y-4">
            {goals.map(({ id, label, desc, emoji }, index) => {
              const isCurrentGoal = currentGoal === id;
              const isLoading = loadingGoal === id;
              const hasGoalProfile = hasAnyProfile(id);
              const configuredCount = getConfiguredIntensities(id).length;
              
              return (
                <motion.button
                  key={id}
                  onClick={() => handleGoalSelect(id)}
                  disabled={loading}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={`w-full p-5 rounded-2xl border-2 flex items-center gap-4 transition-all disabled:opacity-50 ${
                    isCurrentGoal 
                      ? "border-primary bg-primary/10" 
                      : hasGoalProfile
                        ? "border-green-200 bg-green-50 hover:border-primary hover:bg-primary/5"
                        : "border-muted bg-white hover:border-primary hover:bg-primary/5"
                  }`}
                  data-testid={`goal-${id}`}
                >
                  <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center text-2xl relative">
                    {emoji}
                    {isCurrentGoal && (
                      <div className="absolute -top-1 -right-1 w-5 h-5 bg-primary rounded-full flex items-center justify-center">
                        <Check size={12} className="text-white" weight="bold" />
                      </div>
                    )}
                    {!isCurrentGoal && hasGoalProfile && (
                      <div className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                        <Check size={12} className="text-white" weight="bold" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 text-left">
                    <p className="font-bold text-foreground text-lg">{label}</p>
                    <p className="text-sm text-muted-foreground">{desc}</p>
                    {isCurrentGoal && userData?.projection_text && (
                      <div className="flex items-center gap-1 mt-1 text-xs text-primary">
                        <Clock size={12} />
                        <span>{userData.projection_text}</span>
                      </div>
                    )}
                    {hasGoalProfile && (
                      <div className="flex items-center gap-1 mt-1 text-xs text-green-600">
                        <Check size={12} />
                        <span>{configuredCount} {configuredCount === 1 ? 'intensidade configurada' : 'intensidades configuradas'}</span>
                      </div>
                    )}
                    {!hasGoalProfile && !isCurrentGoal && (
                      <div className="flex items-center gap-1 mt-1 text-xs text-amber-600">
                        <Info size={12} />
                        <span>Requer triagem</span>
                      </div>
                    )}
                  </div>
                  {isLoading ? (
                    <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full" />
                  ) : (
                    <ArrowRight size={24} className="text-primary" weight="bold" />
                  )}
                </motion.button>
              );
            })}
          </div>

          {/* Info box */}
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="mt-6 p-4 bg-muted/50 rounded-xl"
          >
            <div className="flex items-start gap-3">
              <Info size={20} className="text-muted-foreground flex-shrink-0 mt-0.5" />
              <div className="text-sm text-muted-foreground">
                <p className="font-medium text-foreground mb-1">Dados isolados por objetivo</p>
                <p>Cada combinação objetivo + intensidade tem os seus próprios dados. Nada se mistura!</p>
              </div>
            </div>
          </motion.div>

          {/* Current stats if available */}
          {userData?.daily_calories && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="mt-4 p-4 bg-primary/5 rounded-2xl border border-primary/20"
            >
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-xs text-muted-foreground">Meta diária atual</p>
                  <p className="text-2xl font-bold text-primary">{userData.daily_calories} kcal</p>
                </div>
                {userData.current_weight && userData.target_weight && currentGoal !== 'manter' && (
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground">Peso atual → meta</p>
                    <p className="font-mono text-foreground">
                      {userData.current_weight} → {userData.target_weight} kg
                    </p>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </motion.div>
      </main>

      {/* Intensity Selection Modal */}
      <AnimatePresence>
        {showIntensityModal && selectedGoal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
            onClick={() => setShowIntensityModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl p-6 max-w-sm w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold">
                  {goals.find(g => g.id === selectedGoal)?.emoji} {goals.find(g => g.id === selectedGoal)?.label}
                </h3>
                <button 
                  onClick={() => setShowIntensityModal(false)}
                  className="p-1 hover:bg-muted rounded-full"
                >
                  <X size={20} />
                </button>
              </div>
              
              <p className="text-sm text-muted-foreground mb-4">
                Escolhe uma intensidade:
              </p>

              <div className="space-y-2">
                {intensities.map(({ id, label, desc }) => {
                  const hasProfile = goalProfiles[selectedGoal]?.[id];
                  const isCurrentConfig = currentGoal === selectedGoal && currentIntensity === id;
                  
                  return (
                    <button
                      key={id}
                      onClick={() => handleGoalWithIntensity(selectedGoal, id)}
                      disabled={loading}
                      className={`w-full p-3 rounded-xl border-2 text-left transition-all flex items-center justify-between ${
                        isCurrentConfig
                          ? "border-primary bg-primary/10"
                          : hasProfile
                            ? "border-green-200 bg-green-50 hover:border-green-400"
                            : "border-muted hover:border-primary/50"
                      }`}
                      data-testid={`intensity-select-${id}`}
                    >
                      <div>
                        <p className="font-medium">{label}</p>
                        <p className="text-xs text-muted-foreground">{desc}</p>
                      </div>
                      {hasProfile ? (
                        <div className="flex items-center gap-1 text-xs text-green-600">
                          <Check size={14} weight="bold" />
                          <span>Configurado</span>
                        </div>
                      ) : (
                        <span className="text-xs text-amber-600">Novo</span>
                      )}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={handleNewIntensity}
                className="w-full mt-4 p-3 rounded-xl border-2 border-dashed border-muted text-muted-foreground hover:border-primary hover:text-primary transition-all text-sm"
              >
                + Nova configuração
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Footer */}
      <footer className="p-4 text-center text-muted-foreground text-sm">
        Nzadi Fit © 2026 – Saúde em Angola 🇦🇴
      </footer>
    </div>
  );
};

export default FocusSelection;
